import React, { useState, useMemo, } from "react"
import { MapLocation, } from "indoor-map-lib"
import { ConnectedProps, connect, } from "react-redux"
import { MenuItemType, } from "../Menu"
import { slice, } from "app/App/redux"
import style from "./style.module.scss"
import WalkIcon from "@material-ui/icons/DirectionsWalk"
import MeetingRoomIcon from "@material-ui/icons/MeetingRoom"
import LocationOnIcon from "@material-ui/icons/LocationOn"
import InfoIcon from "@material-ui/icons/Info"
import PhoneIcon from "@material-ui/icons/Phone"
import SiteIcon from "@material-ui/icons/Language"
import CloseIcon from "@material-ui/icons/Close"
import { mapClient, } from "globalInstance"
import { Link, } from "@material-ui/core"
import { createCongestionIframeUrl, } from "utils/congestion"
import { createRestroomPageUrl, } from "utils/restroom"
import { parseExternalId, } from "utils/location"
import Menu from "@material-ui/core/Menu"
import MenuItem from "@material-ui/core/MenuItem"

const mapDispatch = { ...slice.actions, }
const connector = connect(null, mapDispatch)
type Props = {
  location: MapLocation
} & ConnectedProps<typeof connector>

/**
 * Location情報を表示するコンポーネントです
 */
function LocationCard ({
  location,
  setCurrentMenu,
  setSelectedCategory,
  setCurrentLocation,
}: Props) {
  const [menuAnchorRef, setMenuAnchorRef] = useState<null | HTMLElement>(null)
  const externalService = parseExternalId(location.externalId || "")
  const categoryLabel = useMemo(() => {
    if (location.categories.length > 0) {
      return mapClient.getCategoryLabel(location.categories[0])
    }
    return null
  }, [location])

  const onRouteClick = () => {
    // ルート案内を表示する
    setCurrentMenu(MenuItemType.ROUTE)
  }

  /**
   * 混雑状況を表示する
   */
  const renderCongestion = () => {
    // externalIdにcoが指定されていない場合は表示対象外
    if (!externalService.congestionKey) return

    const src = createCongestionIframeUrl(externalService.congestionKey)
    return (
      <div className={style.congestion}>
        <span>待ち時間</span><iframe title="congestion" src={src}></iframe>
      </div>
    )
  }

  /**
   * カテゴリー名を押下した場合の処理
   */
  const onCategoryClick = () => {
    if (!location.categories) return

    // カテゴリーを選択状態にして施設一覧を表示する
    const category = location.categories[0]
    setCurrentMenu(MenuItemType.FACILITY)
    setSelectedCategory(category)
  }

  const renderCategoryInfo = () => {
    // 混雑状況が表示される場合は表示しない
    if (externalService.congestionKey) return

    if (categoryLabel) {
      return (
        <div className={style.category} onClick={onCategoryClick}>
          {categoryLabel}
        </div>
      )
    }
  }

  const onAvailableClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // 新規ウィンドウでiot-toiletを開く
    if (externalService.restroomIds.length === 0) {
      return
    }
    if (externalService.restroomIds.length === 1) {
      const url = createRestroomPageUrl(externalService.restroomIds[0])
      window.open(url)
    } else {
      setMenuAnchorRef(e.currentTarget)
    }
  }

  /**
   * 空室状況を表示
   */
  const renderAvailability = () => {
    if (externalService.restroomIds.length === 0) return

    const onItemClick = (index: number) => {
      const url = createRestroomPageUrl(externalService.restroomIds[index])
      window.open(url)
      setMenuAnchorRef(null)
    }

    return (
      <div>
        <div
          className={style.container}
          onClick={onAvailableClick}
        >
          <div className={style.icon}><MeetingRoomIcon /></div>
          <div className={style.label}>空室状況</div>
        </div>
        {externalService.restroomIds.length > 1 && (
          <Menu
            anchorEl={menuAnchorRef}
            open={Boolean(menuAnchorRef)}
            onClose={() => {
              setMenuAnchorRef(null)
            }}
          >
            {externalService.restroomIds.map((_, i) => {
              return (
                <MenuItem key={i} onClick={() => onItemClick(i)}>{`エリア ${i + 1}`}</MenuItem>
              )
            })}
          </Menu>
        )}
      </div>
    )
  }

  const renderRouteGuide = () => {
    return (
      <div className={style.container} onClick={onRouteClick}>
        <div className={style.icon}><WalkIcon /></div>
        <div className={style.label}>案内</div>
      </div>
    )
  }

  const renderLogo = () => {
    // ロゴ画像が設定されている場合は表示
    if (location.logo) {
      return (
        <div className={style.logo}>
          <img alt="logo" src={location.logo.original} />
        </div>
      )
    }

    // ロゴがない場合はデフォルト画像を表示
    return (
      <div className={style.logo}>
        <div className={style.notFound}>
          <LocationOnIcon />
        </div>
      </div>
    )
  }

  const renderDetail = () => {
    // 詳細情報が存在しない場合は表示しない
    if (!location.description && !location.phone && !location.url) return

    return (
      <div className={style.detail}>
        {(location.description) && (<div className={style.section}>
          <InfoIcon className={style.icon} />
          <div className={style.text}>
            {location.description}
          </div>
        </div>)}
        {(location.phone) && (<div className={style.section}>
          <PhoneIcon className={style.icon} />
          <div className={style.text}>
            <Link
              color="secondary"
              underline="always"
              href={`tel:${location.phone.number}`}
            >{location.phone.number}</Link>
          </div>
        </div>)}
        {(location.url) && (<div className={style.section}>
          <SiteIcon className={style.icon} />
          <div className={style.text}>
            <Link
              target="_blank"
              color="secondary"
              underline="always"
              href={location.url}
            >{location.url}</Link>
          </div>
        </div>)}
      </div>
    )
  }

  /**
   * 閉じるボタン押下時の処理
   */
  const onCloseClick = () => {
    // 選択状態を解除する
    mapClient.clearAllLocationColors()
    setCurrentLocation(null)
  }

  return (
    <div className={style.frame}>
      <div className={style.summary}>
        {renderLogo()}
        <div className={style.close} onClick={onCloseClick} >
          <CloseIcon />
        </div>
        <div className={style.info}>
          <div className={style.name}>{location.name}</div>
          <div className={style.misc}>
            {renderCongestion()}
            {renderCategoryInfo()}
          </div>
        </div>
        <div className={style.guide}>
          {renderAvailability()}
          {renderRouteGuide()}
        </div>
      </div>
      {renderDetail()}
    </div>
  )
}

export default connector(LocationCard)
